import type { ReactNode } from 'react';

import { copy as sharedUiCopy } from '@shared/copy';
import { Skeleton, SkeletonFields, SkeletonHead } from '@shared/ui/Skeleton';

/**
 * The pending state of `/content/:contentItemId/edit`.
 *
 * The two-column authoring layout at its own template — the rail's `20rem`
 * track first, exactly as the tree orders it, so the phone reading order is the
 * placeholder's reading order too.
 *
 * The rail's three sections are NOT interchangeable boxes: the state card is a
 * heading with one link at the reading end over a fact list, the cover section
 * is a heading over a 16:9 frame with its upload control, and the credits
 * section is a heading over a list of people. Drawing all three as filled
 * panels reserved the right space and left the operator watching three grey
 * rectangles for the length of the read.
 *
 * The form column keeps its five label-and-control pairs and gains the action
 * row that closes it, which arrives with the form rather than after it.
 */
function RailSectionSkeleton({ children }: { readonly children: ReactNode }) {
  return (
    <section aria-hidden="true" className="grid gap-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <Skeleton className="h-7 w-[7rem]" />
        <Skeleton className="ms-auto h-6 w-[5rem] rounded-sm" />
      </div>
      {children}
    </section>
  );
}

export function ContentEditSkeleton() {
  return (
    <>
      <p role="status" className="sr-only">
        {sharedUiCopy.state.loading}
      </p>

      <SkeletonHead />

      <div className="grid gap-8 lg:grid-cols-[20rem_minmax(0,1fr)] lg:items-start lg:gap-9">
        <div className="grid gap-6">
          {/* The publish-state card, inside its own panel. */}
          <div className="grid gap-3 rounded-md border border-border bg-surface p-4">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <Skeleton className="h-7 w-[6rem]" />
              <Skeleton className="ms-auto h-6 w-[6rem] rounded-sm" />
            </div>
            <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2">
              {Array.from({ length: 3 }, (_, row) => (
                <div key={row} className="contents">
                  <Skeleton className="h-4 w-[4rem]" />
                  <Skeleton className={row === 0 ? 'h-6 w-[6rem] rounded-pill' : 'h-4 w-1/2'} />
                </div>
              ))}
            </div>
            <Skeleton className="h-[2.75rem] w-full rounded-sm" />
          </div>

          {/* The cover, at the artwork's own 16:9 frame. */}
          <RailSectionSkeleton>
            <Skeleton className="aspect-[16/9] w-full rounded-md" />
          </RailSectionSkeleton>

          {/* The credited people — a run of rows, not a block. */}
          <RailSectionSkeleton>
            <div className="grid gap-2">
              {Array.from({ length: 3 }, (_, row) => (
                <div key={row} className="flex items-center gap-3">
                  <Skeleton className="h-9 w-9 shrink-0 rounded-pill" />
                  <Skeleton className="h-4 flex-1" />
                </div>
              ))}
            </div>
          </RailSectionSkeleton>
        </div>

        <div className="grid gap-6">
          <SkeletonFields rows={5} />

          <div aria-hidden="true" className="grid gap-3 sm:flex sm:flex-wrap">
            <Skeleton className="h-[2.75rem] w-full rounded-sm sm:w-[7rem]" />
            <Skeleton className="h-[2.75rem] w-full rounded-sm sm:w-[8rem]" />
            <Skeleton className="h-[2.75rem] w-full rounded-sm sm:w-[6rem]" />
          </div>
        </div>
      </div>
    </>
  );
}
